import { WSAlert } from "@wingspanhq/fe-component-library";
import { Batch } from "@wingspanhq/wingspan-api-sdk";
import pluralize from "pluralize";

import { useDownloadSkippedRows } from "../../hooks/useDownloadSkippedRows";
import { useSkippedRowsCount } from "../../hooks/useSkippedRowsCount";

type Props = {
  batch: Batch;
  sheetSlug: string;
  /** Singular noun for what the batch imports, e.g. "Payable". */
  entityLabel: string;
};

/**
 * Rows that never made it into the batch while uploading the spreadsheet —
 * whether they failed validation or the API refused them — are silently missing
 * from the import. This alert makes them visible and downloadable, while the
 * import can still be completed.
 */
export const ImportBatchUploadRejectionsAlert: React.FC<Props> = props => {
  const skippedRowsCount = useSkippedRowsCount(props.batch, props.sheetSlug);
  const downloadSkippedRows = useDownloadSkippedRows(props);

  // Also covers the in-flight count, so the alert appears once the number is
  // known rather than rendering a sentence that has to be corrected a
  // round-trip later.
  if (!skippedRowsCount) {
    return null;
  }

  return (
    <WSAlert
      theme="warning"
      icon="alert"
      name="uploadRejections"
      title="Some rows could not be uploaded"
      action={{
        text: "Download skipped rows",
        onClick: downloadSkippedRows
      }}
    >
      {pluralize("row", skippedRowsCount, true)} could not be uploaded and will
      not be imported. You can import the remaining rows and upload the
      corrected ones separately, or start a new import with the full corrected
      file.
    </WSAlert>
  );
};
